import React, { useState, useMemo } from 'react';
import { Layout as AntLayout, Menu, Button, Dropdown, Tag, Space, Drawer, Grid } from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  TeamOutlined,
  CarOutlined,
  DollarOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MenuOutlined,
  AppstoreOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { AnnouncementBanner } from './AnnouncementBanner';

const { Header, Sider, Content } = AntLayout;
const { useBreakpoint } = Grid;

// Role badge colors
const ROLE_COLORS: Record<string, string> = {
  ADMIN: 'red',
  OWNER: 'blue',
  STAFF: 'green',
};

export const Layout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin, hasAnyRole, getHighestRole, logout } = useAuth();
  const screens = useBreakpoint();

  // Mobile: screens.md is false (below 768px)
  const isMobile = !screens.md;

  // Build menu items based on role
  const menuItems = useMemo(() => {
    const items: any[] = [];

    // Dashboard - always visible
    items.push({
      key: '/',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    });

    // Users - visible to ADMIN and OWNER (OWNER sees Staff Management)
    if (isAdmin() || hasAnyRole('OWNER')) {
      items.push({
        key: '/users',
        icon: <UserOutlined />,
        label: isAdmin() ? 'Users' : 'Staff',
      });
    }

    // Organizations - Admin only
    if (isAdmin()) {
      items.push({
        key: '/organizations',
        icon: <TeamOutlined />,
        label: 'Organizations',
      });
    }

    // Parkings - visible to ADMIN, OWNER, STAFF
    if (hasAnyRole('ADMIN', 'OWNER', 'STAFF')) {
      items.push({
        key: '/parkings',
        icon: <CarOutlined />,
        label: 'Parkings',
      });
    }

    // Pricing - visible to ADMIN and OWNER
    if (isAdmin() || hasAnyRole('OWNER')) {
      items.push({
        key: '/pricing',
        icon: <DollarOutlined />,
        label: 'Pricing',
      });
    }

    // Staff Operations - visible to OWNER and STAFF
    if (hasAnyRole('OWNER', 'STAFF')) {
      items.push({
        key: '/operations',
        icon: <AppstoreOutlined />,
        label: 'Operations',
      });
    }

    // Analytics - visible to ADMIN and OWNER
    if (isAdmin() || hasAnyRole('OWNER')) {
      items.push({
        key: '/analytics',
        icon: <BarChartOutlined />,
        label: 'Analytics',
      });
    }

    return items;
  }, [isAdmin, hasAnyRole]);

  const handleMenuClick = (key: string) => {
    navigate(key);
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const highestRole = getHighestRole();

  // Menu component (reused in Sider and Drawer)
  const menuContent = (
    <Menu
      theme="dark"
      mode="inline"
      selectedKeys={[location.pathname]}
      items={menuItems}
      onClick={({ key }) => handleMenuClick(key)}
    />
  );

  const userMenuItems = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
    },
  ];

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      {/* Desktop Sider - hidden on mobile */}
      {!isMobile && (
        <Sider trigger={null} collapsible collapsed={collapsed}>
          <div style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: collapsed ? 8 : 16,
          }}>
            <img
              src="/logo.png"
              alt="ShadesParking"
              style={{
                height: collapsed ? 32 : 36,
                maxWidth: '100%',
                objectFit: 'contain',
              }}
            />
          </div>
          {menuContent}
        </Sider>
      )}

      {/* Mobile Drawer */}
      <Drawer
        title={<img src="/logo.png" alt="ShadesParking" style={{ height: 40 }} />}
        placement="left"
        onClose={() => setMobileMenuOpen(false)}
        open={isMobile && mobileMenuOpen}
        styles={{
          body: { padding: 0, background: '#001529' },
          header: { background: '#001529', color: 'white', borderBottom: '1px solid #002140' },
        }}
        width={250}
      >
        {menuContent}
      </Drawer>

      <AntLayout>
        <Header style={{
          padding: '0 16px',
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          {/* Mobile: hamburger menu, Desktop: collapse toggle */}
          {isMobile ? (
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setMobileMenuOpen(true)}
              style={{ fontSize: '16px', width: 64, height: 64 }}
            />
          ) : (
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ fontSize: '16px', width: 64, height: 64 }}
            />
          )}
          <Space>
            {highestRole && (
              <Tag color={ROLE_COLORS[highestRole] || 'default'}>
                {highestRole}
              </Tag>
            )}
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Button type="text" icon={<UserOutlined />}>
                {isMobile ? '' : (user?.email || 'Admin')}
              </Button>
            </Dropdown>
          </Space>
        </Header>
        <Content style={{
          margin: isMobile ? '12px 8px' : '24px 16px',
          padding: isMobile ? 12 : 24,
          background: '#fff',
          minHeight: 280
        }}>
          <AnnouncementBanner />
          <Outlet />
        </Content>
      </AntLayout>
    </AntLayout>
  );
};
